import { Box, Stack, Typography } from '@mui/material';
import type { BackupPolicyConfig, InventoryResource } from '@repo/api-gw-sdk';

import { BackupPolicyCard } from '@/app/(dashboard)/settings/backup-policies/BackupPolicyCard';
import {
  Accordion,
  AccordionDetails,
  AccordionDivider,
  AccordionSummary,
} from '@/components/accordion';
import { Link } from '@/components/shared/link';
import { useDAL } from '@/contexts/useDAL';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';

import { AttachmentEventsHistory } from './attachmentEventsHistory';

export const BackupPoliciesTab = ({
  entity,
}: {
  entity: InventoryResource;
}) => {
  const dal = useDAL();
  const activePolicies: BackupPolicyConfig[] = entity.backupPolicies || [];
  const { vaults } = useBackupVaults();

  const { data: attachmentEventsResponse, isLoading: isLoadingEvents } =
    dal.inventory.attachmentEvents.useList(entity.id);
  const { data: allPoliciesResponse, isLoading: isLoadingPolicies } =
    dal.backupPolicy.useList();

  const events = attachmentEventsResponse?.events || [];
  const allPolicies = allPoliciesResponse?.backupPolicies || [];

  const getPoliciesText = () => {
    if (activePolicies.length === 0) {
      return (
        <Link href='/settings/backup-policies'>
          No backup policies are applied
        </Link>
      );
    }
    if (activePolicies.length === 1) {
      return '1 backup policy is applied';
    }
    return `${activePolicies.length} backup policies are applied`;
  };

  return (
    <Stack className='mt-[40px] px-[40px]' overflow='auto' height='100%'>
      <Typography className='mb-[24px]'>{getPoliciesText()}</Typography>
      {activePolicies.map((policy) => (
        <Box key={policy.id} className='mb-[12px]'>
          <BackupPolicyCard
            policy={policy}
            vaults={vaults}
            defaultExpanded={activePolicies.length === 1}
          />
        </Box>
      ))}
      <AccordionDivider />
      <Accordion defaultExpanded={activePolicies.length === 0}>
        <AccordionSummary>
          <Typography variant='h4'>Active backup policies history</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ minHeight: '150px' }}>
          <AttachmentEventsHistory
            events={events}
            policies={allPolicies}
            isLoading={isLoadingEvents || isLoadingPolicies}
          />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};
